//@ts-nocheck
import * as Sentry from '@sentry/react';
import { add, compareAsc, differenceInDays, format, parseISO } from 'date-fns';

import { DATE_PICKER_DATE_FORMAT } from '@nw/shared/config';

import { iitiansWebinarConfig } from '../../constants/WebinarCycleConfigs/iitiansWebinarConfig';
import { pinscaleAcademyWebinarConfig } from '../../constants/WebinarCycleConfigs/pinscaleAcademyWebinarConfig';
import { webinarCycleVariants } from '../../constants/WebinarCycleVariantConstants';
import { SegmentEventRegistrationDetails } from '../../stores/models/SegmentEventRegistrationDetails';

import {
  getCurrentWebinarCycleDateInterface,
  getNewWebinarCycleDetailsInterface,
  WebinarRegistrationDetailsType
} from './types';

export const isWebinarDateMatchedWithToday = (webinarDate, today) => {
  return compareAsc(parseISO(webinarDate as string), today) === 0;
};

export const sortTheDatesArray = (webinarInfo) => {
  const webinarDatesArray = webinarInfo;
  webinarDatesArray.sort(function (date1, date2) {
    return compareAsc(
      parseISO(date1.webinar1Date as string),
      parseISO(date2.webinar1Date as string)
    );
  });
  return webinarDatesArray;
};

const getNewWebinarCycleDetails = (
  values: getNewWebinarCycleDetailsInterface
): WebinarRegistrationDetailsType => {
  const {
    initialWebinarCycle,
    currentWebinarCycleDate,
    totalDifferenceInWeeks
  } = values;

  const initialCycle: string = initialWebinarCycle.cycle.match(/\d+/)[0];
  const newCycle: number = parseInt(initialCycle) + totalDifferenceInWeeks;

  const newWebinarCycleDetails: WebinarRegistrationDetailsType = {
    cycle: initialWebinarCycle.cycle.replace(initialCycle, newCycle.toString()),
    webinar1_date: currentWebinarCycleDate
  };

  return newWebinarCycleDetails;
};

const getDifferenceInWeeks = (totalDifferenceInDays: number): number => {
  return Math.ceil(totalDifferenceInDays / 7);
};

export const getDifferenceInDaysFromTwoDate = (
  endDate: Date,
  startDate: Date = new Date()
) => {
  const totalDifferenceInDays: number = differenceInDays(startDate, endDate);

  return totalDifferenceInDays;
};

export const getCurrentWebinarCycleDate = (
  values: getCurrentWebinarCycleDateInterface
): string => {
  const {
    initialWebinarCycle: { webinar1_date },
    totalDifferenceInWeeks
  } = values;

  const webinarCycleDate: Date = add(parseISO(webinar1_date), {
    days: totalDifferenceInWeeks * 7
  });

  return format(webinarCycleDate, DATE_PICKER_DATE_FORMAT);
};

export const getWebinarCycleConfigInfo = (
  webinarConfig: Array<WebinarRegistrationDetailsType>
): Array<WebinarRegistrationDetailsType> => {
  const initialWebinarCycle: WebinarRegistrationDetailsType = {
    ...webinarConfig[0]
  };

  const totalDifferenceInDays: number = getDifferenceInDaysFromTwoDate(
    parseISO(initialWebinarCycle.webinar1_date)
  );

  const totalDifferenceInWeeks: number = getDifferenceInWeeks(
    totalDifferenceInDays
  );

  const currentWebinarCycleDate: string = getCurrentWebinarCycleDate({
    initialWebinarCycle,
    totalDifferenceInWeeks
  });

  const newWebinarCycle: WebinarRegistrationDetailsType =
    getNewWebinarCycleDetails({
      initialWebinarCycle,
      currentWebinarCycleDate,
      totalDifferenceInWeeks
    });

  webinarConfig.push(newWebinarCycle);

  return webinarConfig;
};

export const getWebinarCycleConfigForVariant = (
  webinarCycleVariant?: string
) => {
  switch (webinarCycleVariant) {
    case webinarCycleVariants.nwcWebinar:
      return getWebinarCycleConfigInfo(pinscaleAcademyWebinarConfig);
    default:
      return getWebinarCycleConfigInfo(iitiansWebinarConfig);
  }
};

export const getUpComingWebinarDetails = (
  webinarInfo: Array<SegmentEventRegistrationDetails>,
  today
) => {
  return webinarInfo.findIndex((eachWebinarInfo) => {
    return (
      compareAsc(parseISO(eachWebinarInfo.webinar1Date as string), today) === 1
    );
  });
};

export const getMatchedWebinarInfo = (
  webinarInfo: Array<SegmentEventRegistrationDetails>,
  isSpotRegistration: boolean,
  date?: Date
) => {
  const webinarDatesArray = sortTheDatesArray(webinarInfo);
  const today = date ? date : new Date();
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const matchedWebinarInfo = webinarDatesArray.find((eachWebinarInfo) => {
    return isWebinarDateMatchedWithToday(
      eachWebinarInfo.webinar1Date,
      todayDate
    );
  });
  if (matchedWebinarInfo) {
    return matchedWebinarInfo;
  } else {
    const upComingWebinarDetailsIndex = getUpComingWebinarDetails(
      webinarDatesArray,
      todayDate
    );
    if (
      upComingWebinarDetailsIndex === 0 ||
      upComingWebinarDetailsIndex === -1
    ) {
      Sentry.captureException(
        'Upcoming or Past webinar details were not updated'
      );
    } else {
      return webinarDatesArray[upComingWebinarDetailsIndex];
    }
  }
};

export const getCycleData = (cycleConfigVariant?: string) => {
  const params = new URLSearchParams(window.location.search);
  const webinarConfig = getWebinarCycleConfigForVariant(cycleConfigVariant);
  const webinarInfo = webinarConfig.map(
    (eachWebinarInfo) => new SegmentEventRegistrationDetails(eachWebinarInfo)
  );
  const matchedWebinarConfig: any = getMatchedWebinarInfo(
    webinarInfo,
    params.get('is_spot_registration') === 'true'
  );
  return matchedWebinarConfig.cycle;
};
